import React, { useEffect, useMemo, useState } from 'react';
import { Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReportFileFiltersType } from './hooks/useTmsReportFileQuery';
import { ReportFileType } from './graphql/queries/getReportFileListingForTms';
import { useCentralReportFileQuery } from './hooks/useTmsReportFileQuery';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import TmsReportFileFilter from './components/TmsReportFileFilter';
import CentralReportFileColumns from './components/CentralReportFileColumns';
import { ReportFileTypeEnum } from '../../../enums/ReportFileEnum';
import { getLastWeekDateRange } from '../../../utils/dateTimeUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { useLazyQuery } from '@apollo/client';
import {
    GET_TMS_BATCH_REPORT_DOWNLOAD_URLS,
    GetTmsBatchReportDownloadUrlsResponse,
    GetTmsBatchReportDownloadUrlsVariables
} from './graphql/queries/getTmsBatchReportDownloadUrls';

const TMS_DEFAULT_TYPE = ReportFileTypeEnum.TMS_TASK;

const initialFilters: ReportFileFiltersType = {
    type: TMS_DEFAULT_TYPE,
    name: undefined,
    reportDate: undefined,
};

const TmsDownloadPage: React.FC = () => {
    const { t } = useTranslation();
    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(initialFilters);
    const [filters, setFilters] = useState<ReportFileFiltersType>(initialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;

    const shouldSkipQuery = !warehouseId;
    const defaultReportDate = useMemo(() => getLastWeekDateRange(), []);

    const effectiveFilters = useMemo(() => ({
        ...filters,
        reportDate: filters.reportDate === undefined ? defaultReportDate : filters.reportDate,
    }), [filters, defaultReportDate]);

    const {
        reportFileData,
        loading: reportLoading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useCentralReportFileQuery(effectiveFilters, currentPage, pageSize, warehouseId, shouldSkipQuery);

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ReportFileType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const [getBatchDownloadUrls] = useLazyQuery<
        GetTmsBatchReportDownloadUrlsResponse,
        GetTmsBatchReportDownloadUrlsVariables
    >(GET_TMS_BATCH_REPORT_DOWNLOAD_URLS);

    const handleResetFilters = () => {
        const resetFilters = { ...initialFilters, reportDate: undefined };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (!warehouseId) return;
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize, warehouseId]);

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const generateFilename = (index: number, reportName?: string) => {
        const timestamp = new Date().toISOString().split('T')[0];
        if (reportName) {
            const hasXlsx = /\.xlsx$/i.test(reportName);
            return hasXlsx ? reportName : `${reportName}.xlsx`;
        }
        return `Report_${index + 1}_${timestamp}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning('Please select the report you wish to download');
            return;
        }
        if (!warehouseId) {
            return;
        }
        setDownloadLoading(true);
        try {
            const reportIds = selectedRows.map(report => parseInt(report.id.toString(), 10));
            const { data } = await getBatchDownloadUrls({
                variables: { reportIds, warehouseId },
                fetchPolicy: 'network-only'
            });
            if (!data?.getTmsBatchReportDownloadUrls) {
                message.error('Failed to obtain the download link.');
                return;
            }
            const downloadUrls = data.getTmsBatchReportDownloadUrls;
            if (downloadUrls.length === 0) {
                message.warning('No available download link is available.');
                return;
            }
            if (downloadUrls.length > 1) {
                message.info(`Starting batch download of ${downloadUrls.length} files...`);
            }
            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                const filename = generateFilename(i, reportName);
                try {
                    await downloadFile(url, filename);
                    await new Promise(res => setTimeout(res, 500));
                    message.success(`File ${i + 1} downloaded`);
                } catch (e) {
                    console.error('Failed to download file', e);
                    message.error(`Failed to download file ${i + 1}`);
                }
            }
            message.success(`All ${downloadUrls.length} files download initiated successfully!`);
        } catch (error) {
            console.error('Batch download failed:', error);
            message.error('Batch download failed, please try again later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <TmsReportFileFilter
                filters={tempFilters}
                setFilters={setTempFilters}
            />
            <div className="flex justify-end gap-2 mb-3">
                <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                    <span className="font-bold">{t('central.download.filters.filter')}</span>
                </Button>
                <Button onClick={handleResetFilters} className="w-auto min-w-24">
                    <span className="font-bold">{t('central.download.filters.reset')}</span>
                </Button>
                <Button
                    type="primary"
                    onClick={handleBatchDownload}
                    loading={downloadLoading}
                    disabled={selectedRows.length === 0}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">
                        {t('central.download.filters.batchDownload')} ({selectedRows.length})
                    </span>
                </Button>
            </div>
            <CentralReportFileColumns
                reportFileData={reportFileData}
                onSelectChange={handleSelectChange}
                selectedRowKeys={selectedRowKeys}
                loading={reportLoading}
            />
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />
        </div>
    );
};

export default TmsDownloadPage;
